// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { findNestedSubRowsProp } from '@shared/ui-kit/utils/find-nested-subrows-prop';
import { TickerTagFeature } from '@shared/data-access/interfaces/api/engine/citec';
import { useMemo } from 'react';
import { useAppSelector } from '@advisor-app/store/store';
import {
  selectInvestmentsTableData,
  selectSummary,
} from '@advisor-app/store/slices/broker';
import { selectAllTags } from '@shared/data-access/slices/tag-manager';

export type DisplayTag = {
  name: string;
  investment: number;
  unrealizedCapitalGains: number;
  unrealizedCapitalGainsPercentage: number;
  investmentPercentage: number;
  progressBarValue: number;
};

export const useTagComposition = () => {
  const tags = useAppSelector(state =>
    selectAllTags(state, TickerTagFeature.PortfolioPositions),
  );

  const investmentData = useAppSelector(selectInvestmentsTableData);

  const subRowsData = investmentData.flatMap(({ subRows }) => subRows);

  const hasNoTags = useMemo(() => tags.length === 0, [tags.length]);

  const { aum } = useAppSelector(selectSummary);

  const displayTags: DisplayTag[] = useMemo(() => {
    return tags
      .map(({ name, tickers }) => {
        let investment = 0;
        let maxInvestment = 0;
        let unrealizedCapitalGains = 0;

        const validTickers = tickers.filter(ticker => ticker && ticker !== 'ALL');

        validTickers.forEach(ticker => {
          const assetInvestment = findNestedSubRowsProp<number>(
            ticker,
            subRowsData,
            'investment',
          );
          investment += assetInvestment || 0;
          maxInvestment = Math.max(maxInvestment, assetInvestment || 0);

          const assetUnrealizedGains = findNestedSubRowsProp<number>(
            ticker,
            subRowsData,
            'unrealized_capital_gains',
          );
          unrealizedCapitalGains += assetUnrealizedGains || 0;
        });

        return {
          name,
          investment,
          unrealizedCapitalGains,
          investmentPercentage: investment / aum,
          progressBarValue: maxInvestment > 0 ? investment / maxInvestment : 0,
          unrealizedCapitalGainsPercentage:
            investment > 0 ? unrealizedCapitalGains / investment : 0,
        };
      })
      .sort((a, b) => b.investmentPercentage - a.investmentPercentage);
  }, [aum, subRowsData, tags]);

  return { displayTags, hasNoTags };
};
